import { getFlagValue } from '@databases/feature-flags/manager';
import { z } from 'zod';

import {
  PageReference,
  pageReferenceValidator,
  spaceReferenceValidator,
  templateReferenceValidator,
} from '../confluence/types';
import {
  BacklinkDisplayType,
  CUSTOM_TEMPLATE_EMPTY,
  CUSTOM_TEMPLATE_ENTRY_MACRO,
  DisplayedDetailType,
  NumberFormats,
  ParentPageOption,
  ParentPageType,
  backlinkDisplayTypeValidator,
  displayedDetailValidator,
  numberFormatValidator,
  parentPageOptionValidator,
  parentPageTypeValidator,
  predefinedNumberFormats,
  selectOptionValidator,
} from '../database/types';
import {
  booleanStringValidator,
  jsonStringValidator,
  transformValidator,
} from '../validators';

export const spaceKeyOptionalValidator = transformValidator(
  z.string().optional(),
  (val) => val && { key: val },
  spaceReferenceValidator.optional()
);

const pageOptionalValidator = transformValidator(
  z.string().optional(),
  (val): PageReference | undefined => {
    const idx = val?.indexOf(':');
    return !val
      ? undefined
      : idx === -1
      ? { type: 'page', spaceKey: '', contentTitle: val }
      : {
          type: 'page',
          spaceKey: val.slice(0, idx!),
          contentTitle: val.slice(idx! + 1),
        };
  },
  pageReferenceValidator.optional()
);

/**
 * Validators for the DB_FIELD macro parameters that will be parsed as field settings. All parameters should be
 * optional (for backwards compatibility).
 */
export const fieldSettingsValidator = z.object({
  newlines: booleanStringValidator(z.boolean().default(false)),
  parentPage: pageOptionalValidator.or(
    z.object({
      contentId: z.string(),
    })
  ),
  parentPageOption: parentPageOptionValidator.default(ParentPageOption.FIXED),
  isParentPageSourceDatabase: booleanStringValidator(z.boolean().optional()),
  parentPageType: parentPageTypeValidator.default(ParentPageType.PAGE),
  parentPageFieldId: z.string().optional(),
  pageTemplate: jsonStringValidator(
    templateReferenceValidator.default(() =>
      getFlagValue('databases.frontend.update-create-page-from-database', false)
        ? CUSTOM_TEMPLATE_EMPTY
        : CUSTOM_TEMPLATE_ENTRY_MACRO
    )
  ),
  displayEntryOnPage: booleanStringValidator(z.boolean().optional()),
  databaseSpace: spaceKeyOptionalValidator,
  databaseId: z.string().optional(),
  multi: booleanStringValidator(z.boolean().default(false)),
  selectOptions: jsonStringValidator(z.array(selectOptionValidator).default(() => [])),
  /**
   * Whether pages/select options can be created through the field. The default is true for select fields
   * and false for page fields. For legacy reasons, the default is true for page fields that have a parent
   * page configured.
   */
  creatable: booleanStringValidator(z.boolean().optional()),
  resolve: booleanStringValidator(z.boolean().default(true)),
  /** A field reference in the same database, for example for a look up field */
  fieldId: z.string().optional(),
  /** For a lookup field, the field in the target database whose value should be shown */
  lookupFieldId: z.string().optional(),
  backlinkDisplayType: backlinkDisplayTypeValidator.default(BacklinkDisplayType.LINK),
  /** For a page labels field, allow editing of the labels */
  allowEdit: booleanStringValidator(z.boolean().default(true)),
  /** For a number field, defines the format */
  numberFormat: jsonStringValidator(
    numberFormatValidator.default(predefinedNumberFormats[NumberFormats.NUMBER])
  ),
  /** @deprecated The server ID of the Jira instance application link. */
  jiraServerId: z.string().optional(),
  /** The name of the Jira field whose value to look up */
  jiraField: z.string().optional(),
  /** The cloud ID of the data source instance */
  cloudId: z.string().optional(),
  /** @deprecated The displayed detail of the page detail field */
  displayedDetail: displayedDetailValidator.default(DisplayedDetailType.CREATED_AT),
  /** The field used for displaying data in the content detail field */
  contentField: z.string().optional(),
  /** Include the version comment for the current version */
  includeVersionComment: booleanStringValidator(z.boolean().optional()),
  contentId: z.string().optional(),
  defaultEnabled: booleanStringValidator(z.boolean().default(false)),
});
